import { z } from 'zod';
import { chunkAnchorSchema } from './rag-anchors';
import {
  webResearchActivitySchema,
  webSourceKindSchema,
} from './web-tools';

const isoDateTimeSchema = z.string().datetime({
  error: 'La date doit etre au format ISO 8601.',
});

export const conversationIdSchema = z.uuid({
  error: 'L identifiant de conversation est invalide.',
});

export const chatMessageRoleSchema = z.enum(['user', 'assistant', 'system'], {
  error: 'Le role du message est invalide.',
});

export const chatPartStateSchema = z.enum(['streaming', 'done']);

export const chatDocumentKindSchema = z.enum(['assistant-brief', 'attachment'], {
  error: 'Le type de document du chat est invalide.',
});

export const chatDocumentStatusSchema = z.enum(['idle', 'streaming', 'ready', 'error'], {
  error: 'Le statut du document du chat est invalide.',
});

export const chatDocumentExportFormatSchema = z.enum(['docx', 'pdf'], {
  error: 'Le format d export du document est invalide.',
});

export const chatDocumentSchema = z.object({
  content: z.string(),
  documentId: z.string().trim().min(1, {
    error: 'L identifiant du document du chat est requis.',
  }),
  filename: z.string().trim().min(1).nullable().optional(),
  kind: chatDocumentKindSchema,
  mediaType: z.string().trim().min(1).nullable().optional(),
  sourceUrl: z.string().trim().min(1).nullable().optional(),
  status: chatDocumentStatusSchema,
  summary: z.string().trim().min(1).nullable().optional(),
  title: z.string().trim().min(1, {
    error: 'Le titre du document du chat est requis.',
  }).max(160, {
    error: 'Le titre du document du chat doit contenir au maximum 160 caracteres.',
  }),
});

export const chatDocumentExportRequestSchema = z.object({
  format: chatDocumentExportFormatSchema,
});

export const chatDocumentExportResultSchema = z.object({
  contentType: z.string().trim().min(1, {
    error: 'Le type de contenu exporte est requis.',
  }),
  documentId: z.string().trim().min(1, {
    error: 'L identifiant du document exporte est requis.',
  }),
  downloadUrl: z.url({
    error: 'L URL de telechargement du document exporte est invalide.',
  }),
  expiresIn: z.number().int().positive({
    error: 'La duree de validite de l URL signee est invalide.',
  }),
  filename: z.string().trim().min(1, {
    error: 'Le nom du fichier exporte est requis.',
  }),
  format: chatDocumentExportFormatSchema,
  objectKey: z.string().trim().min(1, {
    error: 'La clef de stockage du document exporte est requise.',
  }),
});

export const chatMessageTextPartSchema = z.object({
  state: chatPartStateSchema.optional(),
  text: z.string({
    error: 'Le texte du message est requis.',
  }),
  type: z.literal('text'),
});

export const chatMessageReasoningPartSchema = z.object({
  state: chatPartStateSchema.optional(),
  text: z.string({
    error: 'Le texte de raisonnement est requis.',
  }),
  type: z.literal('reasoning'),
});

const chatToolPartStateSchema = z.enum([
  'input-streaming',
  'input-available',
  'approval-requested',
  'approval-responded',
  'output-available',
  'output-error',
  'output-denied',
]);

const chatToolApprovalSchema = z.object({
  approved: z.boolean().optional(),
  id: z.string().trim().min(1, {
    error: 'L identifiant d approbation du tool est requis.',
  }),
  reason: z.string().optional(),
});

export const chatMessageToolPartSchema = z.object({
  approval: chatToolApprovalSchema.optional(),
  callProviderMetadata: z.record(z.string(), z.unknown()).optional(),
  errorText: z.string().optional(),
  input: z.unknown().optional(),
  output: z.unknown().optional(),
  preliminary: z.boolean().optional(),
  providerExecuted: z.boolean().optional(),
  rawInput: z.unknown().optional(),
  resultProviderMetadata: z.record(z.string(), z.unknown()).optional(),
  state: chatToolPartStateSchema,
  title: z.string().optional(),
  toolCallId: z.string().trim().min(1, {
    error: 'L identifiant d appel du tool est requis.',
  }),
  type: z.string().regex(/^tool-[a-zA-Z0-9_-]+$/, {
    error: 'Le type du tool UI est invalide.',
  }),
});

export const chatMessageDynamicToolPartSchema = z.object({
  approval: chatToolApprovalSchema.optional(),
  callProviderMetadata: z.record(z.string(), z.unknown()).optional(),
  errorText: z.string().optional(),
  input: z.unknown().optional(),
  output: z.unknown().optional(),
  preliminary: z.boolean().optional(),
  providerExecuted: z.boolean().optional(),
  resultProviderMetadata: z.record(z.string(), z.unknown()).optional(),
  state: chatToolPartStateSchema,
  title: z.string().optional(),
  toolCallId: z.string().trim().min(1, {
    error: 'L identifiant d appel du tool dynamique est requis.',
  }),
  toolName: z.string().trim().min(1, {
    error: 'Le nom du tool dynamique est requis.',
  }),
  type: z.literal('dynamic-tool'),
});

export const chatMessageFilePartSchema = z.object({
  attachmentId: z.uuid({
    error: 'L identifiant de piece jointe est invalide.',
  }).optional(),
  filename: z.string().trim().min(1).optional(),
  mediaType: z.string().trim().min(1, {
    error: 'Le mediaType du fichier est requis.',
  }),
  sizeBytes: z.number().int().positive().optional(),
  type: z.literal('file'),
  url: z.string().trim().min(1).optional(),
}).refine((part) => Boolean(part.attachmentId || part.url), {
  error: 'La piece jointe doit contenir une reference ou une URL legacy.',
});

export const chatMessageSourceUrlPartSchema = z.object({
  citationKey: z.string().regex(/^S[1-9]\d*$/, {
    error: 'La clef de citation web est invalide.',
  }).optional(),
  domain: z.string().trim().min(1).optional(),
  fetched: z.boolean().optional(),
  publishedAt: z.string().trim().min(1).optional(),
  sourceId: z.string().trim().min(1, {
    error: 'L identifiant de source URL est requis.',
  }),
  sourceKind: webSourceKindSchema.optional(),
  title: z.string().trim().min(1).optional(),
  type: z.literal('source-url'),
  url: z.string().trim().min(1, {
    error: 'L URL de la source est requise.',
  }),
}).refine((source) => !source.citationKey || source.fetched === true, {
  error: 'Une source web doit etre recuperee avant de recevoir une clef de citation.',
  path: ['citationKey'],
});

export const chatSourceReferenceSchema = z.object({
  anchor: chunkAnchorSchema.nullable().optional(),
  blockId: z.string().trim().min(1).optional(),
  chunkRerankScore: z.number().optional(),
  endLine: z.number().int().positive().optional(),
  excerpt: z.string().trim().min(1, {
    error: 'L extrait de la source est requis.',
  }).optional(),
  page: z.number().int().positive({
    error: 'La page de la source est invalide.',
  }),
  referenceId: z.string().trim().min(1, {
    error: 'L identifiant de reference est requis.',
  }),
  sectionPath: z.array(z.string()).optional(),
  source: z.enum(['hybrid', 'pageindex_tool']).optional(),
  startLine: z.number().int().positive().optional(),
  isVerifiable: z.boolean().optional(),
});

export const chatCitationAccessStatusSchema = z.enum(['accessible', 'redacted'], {
  error: 'Le statut d’accès à la citation est invalide.',
});

export const chatMessageSourceDocumentPartSchema = z.object({
  accessStatus: chatCitationAccessStatusSchema.optional(),
  anchor: chunkAnchorSchema.nullable().optional(),
  citationBlockId: z.string().trim().min(1).optional(),
  citationDocumentId: z.uuid({
    error: 'L identifiant du document cite est invalide.',
  }).optional(),
  citationPage: z.number().int().nonnegative().optional(),
  excerpt: z.string().trim().min(1).optional(),
  filename: z.string().trim().min(1).optional(),
  mediaType: z.string().trim().min(1, {
    error: 'Le mediaType du document source est requis.',
  }).optional(),
  redactedLabel: z.string().trim().min(1).optional(),
  sourceId: z.string().trim().min(1, {
    error: 'L identifiant du document source est requis.',
  }),
  references: z.array(chatSourceReferenceSchema).max(12).optional(),
  title: z.string().trim().min(1, {
    error: 'Le titre du document source est requis.',
  }).optional(),
  type: z.literal('source-document'),
  url: z.string().trim().min(1).optional(),
}).superRefine((part, context) => {
  if (part.accessStatus !== 'redacted') {
    return;
  }

  if (!part.redactedLabel) {
    context.addIssue({
      code: 'custom',
      message: 'Le libellé de citation masquée est requis.',
      path: ['redactedLabel'],
    });
  }

  for (const field of ['excerpt', 'filename', 'title', 'url'] as const) {
    if (part[field] !== undefined) {
      context.addIssue({
        code: 'custom',
        message: 'Une citation masquée ne peut pas exposer de métadonnée documentaire.',
        path: [field],
      });
    }
  }
});

export const chatMessageStepStartPartSchema = z.object({
  type: z.literal('step-start'),
});

export const chatConversationDataSchema = z.object({
  conversationId: conversationIdSchema,
  ragEnabled: z.boolean(),
  title: z.string().trim().min(1).max(120).nullable(),
});

export const chatTitleDataSchema = z.object({
  conversationId: conversationIdSchema,
  title: z.string().trim().min(1).max(120).nullable(),
});

export const chatDocumentDeltaSchema = z.object({
  delta: z.string(),
  documentId: z.string().trim().min(1, {
    error: 'L identifiant du document du chat est requis.',
  }),
});

export const chatRagEmptyDataSchema = z.object({
  actionHref: z.literal('/documents'),
  actionLabel: z.string().trim().min(1, {
    error: 'Le libelle d action RAG est requis.',
  }),
});

export const chatMessageDataConversationPartSchema = z.object({
  data: chatConversationDataSchema,
  id: z.string().trim().min(1).optional(),
  type: z.literal('data-conversation'),
});

export const chatMessageDataChatTitlePartSchema = z.object({
  data: chatTitleDataSchema,
  id: z.string().trim().min(1).optional(),
  type: z.literal('data-chat-title'),
});

export const chatMessageDataDocumentPreviewPartSchema = z.object({
  data: chatDocumentSchema,
  id: z.string().trim().min(1).optional(),
  type: z.literal('data-document-preview'),
});

export const chatMessageDataDocumentOpenPartSchema = z.object({
  data: chatDocumentSchema,
  id: z.string().trim().min(1).optional(),
  type: z.literal('data-document-open'),
});

export const chatMessageDataDocumentDeltaPartSchema = z.object({
  data: chatDocumentDeltaSchema,
  id: z.string().trim().min(1).optional(),
  type: z.literal('data-document-delta'),
});

export const chatMessageDataDocumentFinishPartSchema = z.object({
  data: chatDocumentSchema,
  id: z.string().trim().min(1).optional(),
  type: z.literal('data-document-finish'),
});

export const chatMessageDataRagEmptyPartSchema = z.object({
  data: chatRagEmptyDataSchema,
  id: z.string().trim().min(1).optional(),
  type: z.literal('data-rag-empty'),
});

export const chatMessageDataWebResearchActivityPartSchema = z.object({
  data: webResearchActivitySchema,
  id: z.string().trim().min(1).optional(),
  type: z.literal('data-web-research-activity'),
});

// Part streamée par le backend (writeRagCitationsToStream) et conservée dans
// l'historique UI : le client la renvoie telle quelle au POST /api/chat suivant,
// elle doit donc être acceptée par le contrat des parts.
export const chatRagCitationsDataSchema = z.object({
  citations: z.array(chatMessageSourceDocumentPartSchema),
});

export const chatMessageDataRagCitationsPartSchema = z.object({
  data: chatRagCitationsDataSchema,
  id: z.string().trim().min(1).optional(),
  type: z.literal('data-rag-citations'),
});

export const chatDocumentGenerationPendingSchema = z.object({
  title: z.string().trim().min(1).optional(),
});

export const chatMessageDataDocumentGenerationPendingPartSchema = z.object({
  data: chatDocumentGenerationPendingSchema,
  id: z.string().trim().min(1).optional(),
  type: z.literal('data-document-generation-pending'),
});

export const chatMessageDataPartSchema = z.union([
  chatMessageDataConversationPartSchema,
  chatMessageDataChatTitlePartSchema,
  chatMessageDataDocumentPreviewPartSchema,
  chatMessageDataDocumentOpenPartSchema,
  chatMessageDataDocumentDeltaPartSchema,
  chatMessageDataDocumentFinishPartSchema,
  chatMessageDataRagEmptyPartSchema,
  chatMessageDataWebResearchActivityPartSchema,
  chatMessageDataRagCitationsPartSchema,
  chatMessageDataDocumentGenerationPendingPartSchema,
]);

export const chatMessagePartSchema = z.union([
  chatMessageTextPartSchema,
  chatMessageReasoningPartSchema,
  chatMessageToolPartSchema,
  chatMessageDynamicToolPartSchema,
  chatMessageFilePartSchema,
  chatMessageSourceUrlPartSchema,
  chatMessageSourceDocumentPartSchema,
  chatMessageStepStartPartSchema,
  chatMessageDataPartSchema,
]);

export const uiChatMessageSchema = z.object({
  id: z.uuid({
    error: 'L identifiant du message doit etre un UUID.',
  }),
  parts: z.array(chatMessagePartSchema).min(1, {
    error: 'Le message doit contenir au moins une partie.',
  }),
  role: chatMessageRoleSchema,
});

export const documentRevisionContextSchema = z.object({
  baseGenerationId: z.uuid({
    error: 'L identifiant de la version documentaire est invalide.',
  }),
  generatedDocumentId: z.uuid({
    error: 'L identifiant du document généré est invalide.',
  }),
}).strict();

export const chatTurnIdSchema = z.uuid({
  error: 'L identifiant du tour de chat est invalide.',
});

export const sendMessageSchema = z.object({
  conversationId: conversationIdSchema.optional(),
  documentRevisionContext: documentRevisionContextSchema.optional(),
  messages: z.array(uiChatMessageSchema).min(1, {
    error: 'Au moins un message est requis.',
  }),
  ragEnabled: z.boolean().optional(),
  turnId: chatTurnIdSchema.optional(),
  webSearchEnabled: z.boolean().optional(),
});

export const conversationSchema = z.object({
  createdAt: isoDateTimeSchema,
  id: conversationIdSchema,
  lastMessageAt: isoDateTimeSchema,
  lastMessagePreview: z.string().nullable(),
  messageCount: z.number().int().nonnegative(),
  ragEnabled: z.boolean(),
  title: z.string().trim().min(1).max(120).nullable(),
  updatedAt: isoDateTimeSchema,
  webSearchEnabled: z.boolean().default(false),
});

export const conversationFiltersSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(50).default(20),
});

export const persistedChatMessageSchema = z.object({
  content: z.string(),
  conversationId: conversationIdSchema,
  createdAt: isoDateTimeSchema,
  id: z.uuid({
    error: 'L identifiant du message persistant est invalide.',
  }),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  parts: z.array(chatMessagePartSchema).min(1, {
    error: 'Le message persistant doit contenir au moins une partie.',
  }),
  role: chatMessageRoleSchema,
});

export const conversationMessagesSchema = z.object({
  conversation: conversationSchema,
  messages: z.array(persistedChatMessageSchema),
});

export const renameConversationSchema = z.object({
  title: z.string().trim().min(1, {
    error: 'Le titre est requis.',
  }).max(120, {
    error: 'Le titre doit contenir au maximum 120 caracteres.',
  }),
});

export const updateConversationRagSchema = z.object({
  ragEnabled: z.boolean(),
});

export type ChatDocumentDTO = z.infer<typeof chatDocumentSchema>;
export type ChatDocumentDeltaDTO = z.infer<typeof chatDocumentDeltaSchema>;
export type ChatDocumentExportFormatDTO = z.infer<typeof chatDocumentExportFormatSchema>;
export type ChatDocumentExportRequestDTO = z.infer<typeof chatDocumentExportRequestSchema>;
export type ChatDocumentExportResultDTO = z.infer<typeof chatDocumentExportResultSchema>;
export type ChatMessagePartDTO = z.infer<typeof chatMessagePartSchema>;
export type ChatDocumentGenerationPendingDTO = z.infer<typeof chatDocumentGenerationPendingSchema>;
export type ChatMessageDataPartDTO = z.infer<typeof chatMessageDataPartSchema>;
export type ChatMessageDataWebResearchActivityPartDTO = z.infer<typeof chatMessageDataWebResearchActivityPartSchema>;
export type ChatMessageFilePartDTO = z.infer<typeof chatMessageFilePartSchema>;
export type ChatMessageSourceUrlPartDTO = z.infer<typeof chatMessageSourceUrlPartSchema>;
export type ChatRagEmptyDataDTO = z.infer<typeof chatRagEmptyDataSchema>;
export type DocumentRevisionContextDTO = z.infer<typeof documentRevisionContextSchema>;
export type ChatSourceReferenceDTO = z.infer<typeof chatSourceReferenceSchema>;
export type SendMessageDTO = z.infer<typeof sendMessageSchema>;
export type ConversationDTO = z.infer<typeof conversationSchema>;
export type ConversationFiltersDTO = z.infer<typeof conversationFiltersSchema>;
export type ConversationMessagesDTO = z.infer<typeof conversationMessagesSchema>;
export type RenameConversationDTO = z.infer<typeof renameConversationSchema>;
export type UpdateConversationRagDTO = z.infer<typeof updateConversationRagSchema>;
export type PersistedChatMessageDTO = z.infer<typeof persistedChatMessageSchema>;
